import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Sparkles, GraduationCap, BarChart3 } from 'lucide-react';
import { coursesApi } from '../lib/api';
import CourseCard from '../components/ui/CourseCard';
import { SectionLoader } from '../components/ui/PremiumLoader';
import { debounce } from '../lib/utils';
import PremiumDropdown, { DropdownOption } from '../components/ui/PremiumDropdown';
import { useAnalytics, AnalyticsEvents } from '../hooks/useAnalytics';
import { useAuthStore } from '../store/auth';

interface Course {
  id: string;
  slug: string;
  title: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  price: number;
  currency?: string;
  level: string;
  rating?: number;
  totalReviews: number;
  totalStudents: number;
  totalDuration?: number;
  courseType?: 'RECORDED' | 'LIVE';
  isEnrolled?: boolean; // Added for protected browse endpoint
  professor: {
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  category?: {
    name: string;
  };
}

interface Category {
  id: string;
  name: string;
  slug: string;
  _count?: {
    courses: number;
  };
}

// Active Filter Chip
function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full
                 bg-gradient-to-r from-coral/20 to-coral/10
                 border border-coral/30"
    >
      <span className="text-xs font-medium text-coral">{label}</span>
      <button
        onClick={onClear}
        className="p-0.5 rounded-full hover:bg-coral/20 transition-colors"
      >
        <X className="w-3 h-3 text-coral" />
      </button>
    </motion.div>
  );
}

export default function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const { trackEvent, trackSearch } = useAnalytics();
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  
  // Determine if we're in the student dashboard (protected context)
  const isStudentDashboard = location.pathname.startsWith('/student');

  const currentPage = parseInt(searchParams.get('page') || '1');
  const searchQuery = searchParams.get('search') || '';
  const categoryId = searchParams.get('category') || '';
  const level = searchParams.get('level') || '';
  
  // Track page view on mount
  useEffect(() => {
    trackEvent(AnalyticsEvents.COURSES_PAGE_VIEWED, {
      hasFilters: !!(categoryId || level || searchQuery),
      isAuthenticated,
      context: isStudentDashboard ? 'student_dashboard' : 'public',
    });
  }, []); // Only on mount

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await coursesApi.getCategories();
        if (response.data.success) {
          setCategories(response.data.data as Category[] || []);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch courses - use protected endpoint when in student dashboard
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: 12,
          search: searchQuery || undefined,
          categoryId: categoryId || undefined,
          level: level || undefined,
        };
        
        // Use protected browse endpoint when authenticated in student dashboard
        // This provides enrollment status for each course
        const response = isStudentDashboard && isAuthenticated 
          ? await coursesApi.browse(params)
          : await coursesApi.getAll(params);
          
        if (response.data.success) {
          const coursesData = response.data.data as Course[] || [];
          setCourses(coursesData);
          setTotalPages(response.data.meta?.totalPages || 1);
          
          // Track search if there's a query
          if (searchQuery) {
            const filters: Record<string, string> = {};
            if (categoryId) filters.categoryId = categoryId;
            if (level) filters.level = level;
            trackSearch(searchQuery, coursesData.length, filters);
          }
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, [currentPage, searchQuery, categoryId, level, trackSearch, isStudentDashboard, isAuthenticated]);

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      params.set('page', '1');
      setSearchParams(params);
    }, 500),
    [searchParams, setSearchParams]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
      
      // Track filter usage
      if (key === 'category') {
        const categoryName = categories.find(c => c.id === value)?.name;
        trackEvent(AnalyticsEvents.COURSES_FILTERED_BY_CATEGORY, {
          categoryId: value,
          categoryName,
        });
      } else if (key === 'level') {
        trackEvent(AnalyticsEvents.COURSES_FILTERED_BY_LEVEL, {
          level: value,
        });
      }
    } else {
      params.delete(key);
    }
    // Only reset to page 1 when changing filters, not when changing page itself
    if (key !== 'page') {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const clearFilter = (key: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = categoryId || level;

  // Prepare dropdown options
  const categoryOptions: DropdownOption[] = [
    { value: '', label: 'All Categories' },
    ...categories.map(cat => ({
      value: cat.id,
      label: cat.name,
      count: cat._count?.courses,
    })),
  ];

  const levelOptions: DropdownOption[] = [
    { value: '', label: 'All Levels' },
    { value: 'BEGINNER', label: 'Beginner' },
    { value: 'INTERMEDIATE', label: 'Intermediate' },
    { value: 'ADVANCED', label: 'Advanced' },
  ];

  const selectedCategory = categories.find(c => c.id === categoryId);
  const selectedLevel = levelOptions.find(l => l.value === level);

  return (
    <div className="min-h-screen py-6 sm:py-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-10"
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white tracking-tight">
            Browse Courses
          </h1>
          <p className="mt-2 sm:mt-3 text-white/50 text-sm sm:text-lg">
            Discover courses taught by industry experts
          </p>
        </motion.div>

        {/* Search and Filters Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col gap-3 sm:gap-4 mb-6"
        >
          {/* Search */}
          <div className="relative w-full lg:max-w-xl">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-coral/20 to-transparent opacity-0 
                          hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            <input
              type="text"
              placeholder="Search courses..."
              defaultValue={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-11 sm:pl-12 pr-4 py-3 sm:py-3.5 rounded-xl text-white text-sm
                       placeholder:text-white/30 transition-all duration-300
                       border border-white/10 hover:border-white/20
                       focus:outline-none focus:border-coral/50 focus:ring-1 focus:ring-coral/20"
              style={{ background: 'rgba(255,255,255,0.02)' }}
            />
            <Search className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-white/30" />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-row gap-2 sm:gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <PremiumDropdown
              label="Category"
              icon={<GraduationCap className="w-4 h-4" />}
              options={categoryOptions}
              value={categoryId}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="All Categories"
            />

            <PremiumDropdown
              label="Level"
              icon={<BarChart3 className="w-4 h-4" />}
              options={levelOptions}
              value={level}
              onChange={(value) => handleFilterChange('level', value)}
              placeholder="All Levels"
            />
          </div>
        </motion.div>

        {/* Active Filters */}
        <AnimatePresence>
          {hasActiveFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap items-center gap-2 mb-8"
            >
              <span className="text-xs uppercase tracking-wider text-white/30 font-medium mr-2">
                Active Filters:
              </span>
              
              {selectedCategory && (
                <FilterChip
                  label={selectedCategory.name}
                  onClear={() => clearFilter('category')}
                />
              )}
              
              {selectedLevel && level && (
                <FilterChip
                  label={selectedLevel.label}
                  onClear={() => clearFilter('level')}
                />
              )}

              <button
                onClick={clearAllFilters}
                className="ml-2 text-xs text-white/40 hover:text-coral transition-colors
                         flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white/[0.03]"
              >
                <X className="w-3 h-3" />
                Clear all
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        {!isLoading && courses.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 mb-6"
          >
            <Sparkles className="w-4 h-4 text-coral" />
            <span className="text-sm text-white/50">
              Showing <span className="text-white font-medium">{courses.length}</span> courses
              {hasActiveFilters && ' matching your filters'}
            </span>
          </motion.div>
        )}

        {/* Courses grid */}
        {isLoading ? (
          <SectionLoader />
        ) : courses.length > 0 ? (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
            >
              {courses.map((course, index) => (
                <CourseCard key={course.id} course={course} index={index} />
              ))}
            </motion.div>

            {/* Premium Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, currentPage - 1).toString())}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                           disabled:opacity-30 disabled:cursor-not-allowed
                           text-white/50 hover:text-white hover:bg-white/[0.05]"
                >
                  Previous
                </button>
                
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handleFilterChange('page', page.toString())}
                      className={`w-10 h-10 rounded-xl font-medium text-sm transition-all duration-300 ${
                        currentPage === page
                          ? 'text-white shadow-lg'
                          : 'text-white/40 hover:text-white hover:bg-white/[0.05]'
                      }`}
                      style={currentPage === page ? {
                        background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)',
                        boxShadow: '0 4px 20px rgba(220, 70, 74, 0.3)',
                      } : undefined}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handleFilterChange('page', Math.min(totalPages, currentPage + 1).toString())}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                           disabled:opacity-30 disabled:cursor-not-allowed
                           text-white/50 hover:text-white hover:bg-white/[0.05]"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-white/[0.03] 
                          flex items-center justify-center border border-white/10">
              <Search className="w-8 h-8 text-white/20" />
            </div>
            <p className="text-white/60 text-lg font-medium">No courses found</p>
            <p className="text-white/30 mt-2">Try adjusting your search or filters</p>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="mt-6 px-6 py-2.5 rounded-xl text-sm font-medium
                         bg-coral/10 text-coral border border-coral/20
                         hover:bg-coral/20 transition-all duration-200"
              >
                Clear all filters
              </button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
